import { createSignal, Show, untrack } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { keyFromSelector } from "i18next";
import type { InferOutput } from "valibot";
import { boolean, check, forward, object, partialCheck, pipe } from "valibot";
import InviteService from "../../services/InviteService.ts";
import type { InviteSummary } from "../../state/invite.ts";
import { useTranslation } from "../../state/i18n.tsx";
import { Translation } from "../../utils/Translation.tsx";
import moreappWhiteIcon from "../../assets/moreapp-icon-white.svg";
import { TextButton } from "../../components/button/Button.tsx";
import { Checkbox } from "../../components/checkbox/Checkbox.tsx";
import { TextInput } from "../../components/inputs/TextInput.tsx";
import { PasswordInput } from "../../components/inputs/PasswordInput.tsx";
import { ErrorMessage } from "../../components/inputs/Input.tsx";
import TextLink from "../../components/link/TextLink.tsx";
import { errorTranslation, requireText, textValue } from "../../utils/validation/base.ts";
import { MAX_CHARACTERS_PASSWORD, MAX_CHARACTERS_SM, MIN_CHARACTERS_PASSWORD } from "../../constants.ts";
import { Form } from "../../components/form/Form.tsx";
import { createForm } from "../../components/form/createForm.ts";
import Section from "../Section.tsx";
import Layout from "../Layout.tsx";
import InlineMessage from "../../components/inlineMessage/InlineMessage.tsx";
import { directUserToSupport } from "../../utils/supportUtil.ts";
import type { MoreAppError } from "../../MoreAppError.ts";
import { isDesktopViewport, isPhoneOrTabletDevice } from "../../utils/deviceUtil.ts";
import PageHeader from "../PageHeader.tsx";
import type { User } from "../../state/user.tsx";
import type UserId from "../../state/UserId.ts";
import { setShowWelcomeModal } from "../../state/welcomeModal.ts";
import MobileWelcomeSection from "../MobileWelcomeSection.tsx";

type InviteAcceptanceNewUserStateProps = {
  invite: InviteSummary;
  token: string;
};

const InviteAcceptanceNewUserState = (props: InviteAcceptanceNewUserStateProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [completedSignup, setCompletedSignup] = createSignal<User>();

  // A phone/tablet on a wide (desktop-sized) viewport is treated as desktop: there's enough
  // screen space to use the platform directly, so there's no need to push them toward the app.
  const shouldShowMobileWelcome = () => isPhoneOrTabletDevice() && !isDesktopViewport();

  const completeDesktopOnboarding = (userId: UserId) => {
    setShowWelcomeModal(userId);
    navigate("/");
  };

  return (
    <Show
      when={completedSignup()}
      fallback={
        <Layout>
          <PageHeader
            title={t(($) => $.INVITE_PAGE_HEADER_TITLE)}
            subTitle={t(($) => $.INVITE_PAGE_HEADER_SUBTITLE)}
            logoSrc={moreappWhiteIcon}
          />
          <Section centerOnMobile={false} fullHeight>
            <h1 class="mb-6 text-header-xl text-primary">{t(($) => $.INVITE_PAGE_NEW_USER_TITLE)}</h1>
            <NewUserForm
              onSuccess={(user) => {
                if (shouldShowMobileWelcome()) {
                  setCompletedSignup(user);
                } else {
                  completeDesktopOnboarding(user.id);
                }
              }}
              invite={props.invite}
              token={props.token}
            />
          </Section>
        </Layout>
      }
    >
      {(user) => (
        <Layout>
          <PageHeader
            title={t(($) => $.INVITE_PAGE_SUCCESS_HEADER_TITLE, { firstName: user().firstName })}
            subTitle={t(($) => $.INVITE_PAGE_SUCCESS_HEADER_SUBTITLE)}
            user={user()}
          />
          <MobileWelcomeSection openPlatform={() => navigate("/")} />
        </Layout>
      )}
    </Show>
  );
};

type NewUserFormProps = {
  invite: InviteSummary;
  token: string;
  onSuccess: (user: User) => void;
};

const NewUserSchema = pipe(
  object({
    firstName: requireText(MAX_CHARACTERS_SM),
    lastName: requireText(MAX_CHARACTERS_SM),
    password: textValue(MIN_CHARACTERS_PASSWORD, MAX_CHARACTERS_PASSWORD),
    passwordConfirm: textValue(MIN_CHARACTERS_PASSWORD, MAX_CHARACTERS_PASSWORD),
    receiveNewsLetter: boolean(),
    agreeToTerms: pipe(
      boolean(),
      check((agreed) => agreed, errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    ),
  }),
  forward(
    partialCheck(
      [["password"], ["passwordConfirm"]],
      (input) => input.password === input.passwordConfirm,
      errorTranslation(keyFromSelector(($) => $.VALIDATION_CONFIRM_PASSWORD_MISMATCH)),
    ),
    ["passwordConfirm"],
  ),
);

const NewUserForm = (props: NewUserFormProps) => {
  const acceptInviteMutation = InviteService.acceptInvite();
  const [submitError, setSubmitError] = createSignal<MoreAppError | null>(null);
  const [passwordBlacklisted, setPasswordBlacklisted] = createSignal(false);
  const { t } = useTranslation();

  const form = createForm<InferOutput<typeof NewUserSchema>>(
    NewUserSchema,
    ({ firstName, lastName, password, receiveNewsLetter }) =>
      new Promise<void>((resolve, reject) => {
        setSubmitError(null);
        acceptInviteMutation.mutate(
          {
            token: untrack(() => props.token),
            inviteOnboarding: {
              settings: {
                firstName,
                lastName,
                receiveNewsLetter,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                language: untrack(() => props.invite.language),
              },
              password,
            },
          },
          {
            onSuccess: (user) => {
              props.onSuccess(user);
              resolve();
            },
            onError: (err) => {
              // The backend has no dedicated error code for this case (scope is the generic
              // "INVALID_REQUEST_DATA"), so matching on the message text is the only option.
              if (err.message === "Password is blacklisted") {
                setPasswordBlacklisted(true);
              } else {
                setSubmitError(err);
              }
              reject(err);
            },
          },
        );
      }),
    { firstName: "", lastName: "", password: "", passwordConfirm: "", receiveNewsLetter: false, agreeToTerms: false },
  );

  return (
    <Form form={form} class="flex size-full grow flex-col">
      {({ value, error, updateValue }) => (
        <div class="flex h-full grow flex-col">
          <div class="flex w-full flex-col gap-y-6">
            <TextInput
              label={t(($) => $.INVITE_PAGE_NEW_USER_FIRST_NAME_INPUT_LABEL)}
              required
              value={value("firstName")}
              errorMessage={error("firstName")}
              onInput={(e) => updateValue("firstName", e.target.value)}
              onBlur={(e) => updateValue("firstName", e.target.value)}
            />
            <TextInput
              label={t(($) => $.INVITE_PAGE_NEW_USER_LAST_NAME_INPUT_LABEL)}
              required
              value={value("lastName")}
              errorMessage={error("lastName")}
              onInput={(e) => updateValue("lastName", e.target.value)}
              onBlur={(e) => updateValue("lastName", e.target.value)}
            />
            <PasswordInput
              label={t(($) => $.INVITE_PAGE_NEW_USER_PASSWORD_INPUT_LABEL)}
              required
              value={value("password")}
              errorMessage={
                error("password") ??
                (passwordBlacklisted() ? t(($) => $.VALIDATION_CONFIRM_PASSWORD_BLACKLISTED) : undefined)
              }
              onInput={(e) => {
                updateValue("password", e.target.value);
                setPasswordBlacklisted(false);
              }}
              onBlur={(e) => {
                updateValue("password", e.target.value);
                setPasswordBlacklisted(false);
              }}
            />
            <PasswordInput
              label={t(($) => $.INVITE_PAGE_NEW_USER_PASSWORD_CONFIRMATION_INPUT_LABEL)}
              required
              value={value("passwordConfirm")}
              errorMessage={error("passwordConfirm")}
              onInput={(e) => updateValue("passwordConfirm", e.target.value)}
              onBlur={(e) => updateValue("passwordConfirm", e.target.value)}
            />
            <Show when={submitError()}>
              <InlineMessage
                variant="warning"
                label={t(($) => $.INVITE_PAGE_SUBMIT_ERROR_LABEL)}
                action={{
                  label: t(($) => $.INVITE_PAGE_SUBMIT_ERROR_ACTION_LABEL),
                  onClick: directUserToSupport,
                }}
              />
            </Show>
            <Checkbox
              label={t(($) => $.INVITE_PAGE_NEW_USER_NEWSLETTER_CHECKBOX_LABEL)}
              checked={value("receiveNewsLetter") ?? false}
              setChecked={(checked) => updateValue("receiveNewsLetter", checked === true)}
            />
            <div class="flex flex-col gap-y-1">
              <Checkbox
                required
                label={
                  <Translation
                    key={keyFromSelector(($) => $.INVITE_PAGE_NEW_USER_TERMS_CHECKBOX_LABEL)}
                    components={{
                      externalLink: (linkProps) => (
                        <TextLink isExternal href={linkProps.href} label={linkProps.label} />
                      ),
                    }}
                  />
                }
                checked={value("agreeToTerms") ?? false}
                setChecked={(checked) => updateValue("agreeToTerms", checked === true)}
              />
              <ErrorMessage errorMessage={error("agreeToTerms")} />
            </div>
          </div>
          <div class="mt-auto flex justify-end pt-10 mobile:mt-0">
            <TextButton
              text={t(($) => $.INVITE_PAGE_NEW_USER_NEXT_BUTTON_LABEL)}
              type="submit"
              form={form.id}
              loading={form.isSubmitting()}
              disabled={form.submitDisabled()}
              class="w-full mobile:w-auto"
            />
          </div>
        </div>
      )}
    </Form>
  );
};

export default InviteAcceptanceNewUserState;
